import { useQuery } from '@tanstack/react-query'
import { resourcesCopy } from '@/copy/resources'
import { CheckboxField, SelectField, TextareaField } from '@/ui/Field/Field'
import { topicsApi } from '../../api/topics'
import { useWorkspaceAgentsQuery } from '../../hooks/queries'
import { PredicateFields } from './PredicateFields'
import { predicateBody, predicateFields } from './predicate'
import {
  type FieldProps,
  type Fields,
  fieldStr,
  type ResourceFormDescriptor,
} from './shared'

/** Keys the form doesn't edit — a predicate's `exists`, the tag of the
 * recipe that installed the workflow — carried through edits verbatim. */
const PRESERVED_KEYS = ['exists', 'recipe'] as const

/**
 * The workflow form: which agent acts on the events that land in this
 * topic, which events (the shared predicate over the event), and the
 * instructions its turn starts with. Saving makes the agent a member of
 * the topic.
 */
// eslint-disable-next-line react-refresh/only-export-components -- local component; the descriptor below is this file's only export
function WorkflowFields({ fields, set, scope }: FieldProps) {
  const copy = resourcesCopy.forms.workflow
  const { data: topic } = useQuery({
    queryKey: ['topic', scope?.id ?? ''],
    queryFn: () => topicsApi.get(scope?.id ?? ''),
    enabled: scope?.kind === 'topic',
    staleTime: 30_000,
  })
  const { data: agents = [] } = useWorkspaceAgentsQuery(topic?.workspace_id)

  return (
    <>
      <SelectField
        id="workflow-agent"
        label={copy.agent}
        value={fields.agent_id ?? ''}
        onValue={(v) => set('agent_id', v)}
        help={copy.agentHelp}
      >
        <option value="">{copy.chooseAgent}</option>
        {agents
          .filter(
            (a) => a.status === 'active' || a.agent_id === fields.agent_id,
          )
          .map((a) => (
            <option key={a.agent_id} value={a.agent_id}>
              {a.name}
            </option>
          ))}
      </SelectField>

      <PredicateFields fields={fields} set={set} idPrefix="workflow" />

      <TextareaField
        id="workflow-instructions"
        label={copy.instructions}
        help={copy.instructionsHelp}
        value={fields.instructions ?? ''}
        onValue={(v) => set('instructions', v)}
        textareaProps={{
          rows: 5,
          placeholder: copy.instructionsPlaceholder,
        }}
      />

      <CheckboxField
        id="workflow-paused"
        label={copy.paused}
        checked={fields.enabled === 'false'}
        onChecked={(v) => set('enabled', v ? 'false' : '')}
      />
    </>
  )
}

export const workflowForm: ResourceFormDescriptor = {
  Fields: WorkflowFields,
  toBody: (f: Fields) => {
    const body: Record<string, unknown> = {
      agent_id: f.agent_id ?? '',
      ...predicateBody(f),
    }
    const instructions = (f.instructions ?? '').trim()
    if (instructions) body.instructions = instructions
    if (f.enabled === 'false') body.enabled = false
    return body
  },
  fromResource: (d: unknown) => {
    const r = (d ?? {}) as Record<string, unknown>
    return {
      ...predicateFields(d, PRESERVED_KEYS),
      agent_id: fieldStr(d, 'agent_id'),
      instructions: fieldStr(d, 'instructions'),
      enabled: r.enabled === false ? 'false' : '',
    }
  },
}
